import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Navigation, Instagram, Facebook } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MAPS_DIRECTIONS, PHONE_1, PHONE_2 } from "@/i18n/translations";
import { track } from "@/lib/analytics";
import { OpeningHours } from "@/components/OpeningHours";
import { ConsentMap } from "@/components/ConsentMap";

const MAP_SRC = "https://www.google.com/maps?q=Smolensky+1,+Keratsini+18756,+Greece&output=embed";

export const LandingContact = ({ heading }) => {
  const { t } = useLang();
  const c = t.contact;

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-white" data-testid="landing-contact">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-bamboo">{c.tag}</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight">{heading}</h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <a
              href={MAPS_DIRECTIONS}
              target="_blank"
              rel="noreferrer"
              data-testid="landing-address-link"
              onClick={() => track("directions_click", { fb_event: "Contact", location: "landing" })}
              className="group flex items-start gap-3 rounded-2xl bg-brand-cream border border-brand/10 p-5 hover:border-brand transition-colors no-a11y-underline"
            >
              <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-brand-cream shrink-0"><MapPin className="h-5 w-5" /></span>
              <span>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50">{c.address_label}</p>
                <p className="mt-1 font-semibold text-brand-ink text-sm">{c.address}</p>
                <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-bamboo-deep"><Navigation className="h-3.5 w-3.5" /> {c.get_directions}</span>
              </span>
            </a>

            <div className="flex items-start gap-3 rounded-2xl bg-brand-cream border border-brand/10 p-5">
              <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-brand-cream shrink-0"><Phone className="h-5 w-5" /></span>
              <span>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50">{c.phone_label}</p>
                <div className="mt-1 flex flex-col">
                  <a href={`tel:+30${PHONE_1}`} data-testid="landing-phone-1" onClick={() => track("call_click", { fb_event: "Contact", number: PHONE_1, location: "landing" })} className="font-semibold text-brand-ink text-sm hover:text-brand transition-colors">{PHONE_1}</a>
                  <a href={`tel:+30${PHONE_2}`} data-testid="landing-phone-2" onClick={() => track("call_click", { fb_event: "Contact", number: PHONE_2, location: "landing" })} className="font-semibold text-brand-ink text-sm hover:text-brand transition-colors">{PHONE_2}</a>
                </div>
              </span>
            </div>

            <a
              href="mailto:care4me.gr@gmail.com"
              data-testid="landing-email-link"
              onClick={() => track("email_click", { fb_event: "Contact", location: "landing" })}
              className="flex items-start gap-3 rounded-2xl bg-brand-cream border border-brand/10 p-5 hover:border-brand transition-colors no-a11y-underline"
            >
              <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-brand-cream shrink-0"><Mail className="h-5 w-5" /></span>
              <span>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50">{c.email_label}</p>
                <p className="mt-1 font-semibold text-brand-ink text-sm">care4me.gr@gmail.com</p>
              </span>
            </a>

            <div className="rounded-2xl overflow-hidden border border-brand/10 h-[200px]">
              <ConsentMap src={MAP_SRC} title="Care For Me location" testid="landing-map" />
            </div>

            <div className="flex gap-3 pt-1">
              <a href="https://www.instagram.com/careforme.gr/" target="_blank" rel="noreferrer" aria-label="Instagram" className="grid place-items-center h-11 w-11 rounded-full bg-brand-soft/60 text-brand hover:bg-brand hover:text-brand-cream transition-colors"><Instagram className="h-5 w-5" /></a>
              <a href="https://www.facebook.com/profile.php?id=61588405945105&locale=el_GR" target="_blank" rel="noreferrer" aria-label="Facebook" className="grid place-items-center h-11 w-11 rounded-full bg-brand-soft/60 text-brand hover:bg-brand hover:text-brand-cream transition-colors"><Facebook className="h-5 w-5" /></a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <OpeningHours />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
